import { useState, useEffect } from 'react';
import { api, OrgInfo } from '../api';
import { getOrgSlug } from '../utils/getOrgSlug';
import { getErrorMessage, UnauthorizedError } from '../../../shared/src';

interface LoginPageProps {
  onLogin: () => void;
  onPasswordLogin: (token: string) => void;
  /** Optional one-time notice shown above the form (e.g. after a session-expiry redirect). */
  notice?: string | null;
}

/**
 * The server's own message, plus how many attempts remain when it said so — five failures
 * lock the account for 30 minutes, and a user who only sees "Unauthorized" walks into that.
 */
export function describeLoginError(err: unknown): string {
  const message = getErrorMessage(err);
  if (err instanceof UnauthorizedError && typeof err.remainingAttempts === 'number') {
    const n = err.remainingAttempts;
    return `${message} (${n} attempt${n === 1 ? '' : 's'} left before the account is locked)`;
  }
  return message;
}

export function LoginPage({ onLogin, onPasswordLogin, notice }: LoginPageProps) {
  const orgSlug = getOrgSlug();
  const [orgInfo, setOrgInfo] = useState<OrgInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Password auth state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadOrgInfo();
  }, []);

  const loadOrgInfo = async () => {
    try {
      const info = await api.getOrgInfo(orgSlug);
      setOrgInfo(info);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const result = await api.passwordLogin(email, password, orgSlug);
      api.setToken(result.token);
      onPasswordLogin(result.token);
    } catch (err: unknown) {
      setError(describeLoginError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !orgInfo) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Organization Not Found</h1>
          <p className="error-text">{error}</p>
        </div>
      </div>
    );
  }

  const isPasswordAuth = orgInfo?.auth_method === 'password';

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>Welcome to Fakto</h1>
        <p className="subtitle">
          {isPasswordAuth
            ? 'Sign in with your email and password'
            : 'Sign in to access your organization dashboard'}
        </p>

        {notice && <div className="notice-message">{notice}</div>}

        {isPasswordAuth ? (
          <form onSubmit={handlePasswordLogin}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="you@company.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {error && <div className="error-message">{error}</div>}
            <button
              type="submit"
              className="btn-primary btn-full"
              disabled={submitting}
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>
            <a href="/forgot-password" className="forgot-password-link">
              Forgot your password?
            </a>
          </form>
        ) : (
          <>
            <button className="btn-primary btn-full" onClick={onLogin}>
              Sign In with SSO
            </button>
            {error && <div className="error-message">{error}</div>}
          </>
        )}

        <p className="org-hint">
          Organization: <strong>{orgInfo?.name || orgSlug}</strong>
        </p>
      </div>

      <style>{`
        .form-group {
          margin-bottom: 16px;
          text-align: left;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #333;
        }

        .form-group input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
        }

        .form-group input:focus {
          outline: none;
          border-color: #4a90a4;
          box-shadow: 0 0 0 2px rgba(74, 144, 164, 0.1);
        }

        .error-message {
          margin-bottom: 16px;
          padding: 10px;
          background-color: #f8d7da;
          border-radius: 4px;
          color: #721c24;
          font-size: 14px;
        }

        .error-text {
          color: #dc3545;
        }

        .notice-message {
          margin-bottom: 16px;
          padding: 10px;
          background-color: #fff3cd;
          border-radius: 4px;
          color: #664d03;
          font-size: 14px;
        }

        .forgot-password-link {
          display: block;
          margin-top: 16px;
          color: #4a90a4;
          text-decoration: none;
          font-size: 14px;
        }

        .forgot-password-link:hover {
          text-decoration: underline;
        }

        .password-input-wrapper {
          position: relative;
        }

        .password-input-wrapper input {
          padding-right: 40px;
        }

        .password-toggle-btn {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: #999;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .password-toggle-btn:hover {
          color: #333;
        }

        .password-toggle-btn:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
}
